/* eslint-disable */
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { MenuLinkItem } from '../../../../../components/Template/Header'
import SearchLoader from '../../../../../components/Template/Search/SearchLoader'
import SearchPopover from '../../../../../components/Template/Search/SearchPopover'
import SearchInput from '../../../../../components/Template/Search/SearchInput'
import SummaryBar from '../../../../../components/Template/Search/SummaryBar'
import { IEvent } from '../../../../../models/Event'
import { IWithSize } from '../../../../BookingCalendar/Calendar/Calendar'
import { useSelector } from 'react-redux'
import { IStore } from '../../../../../reducers'
import { useDebounce } from 'react-use'
import ResultsList from './ResultsList'
import { Mixpanel } from '../../../../../utils/Mixpanel'
import { getUserId, getUserPriceGroup, isUserNewPremium } from '../../../../../reducers/userAccount/selectors/userAccount'
import {
  isAllInOneLicenseValid,
  isLicenseValid,
} from '../../../../../reducers/userAccount/selectors/license'
import { PopoveredIcon2 } from '../../../../../components/PopoveredIcon/PopoveredIcon'
import { getUserSummary } from '../../../../../reducers/userObject/selectors/settings'
import UpgradePremiumBlockScreenModal from '../../../../UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'
import { useMediaQuery, useTheme } from '@material-ui/core'

const CHARACTERS_THRESHOLD = 3
const MAX_SEARCH_RESULTS = 200

const Search: FunctionComponent<
  ISearchProps & ISearchStateProps & ISearchDispatchProps & IWithSize
> = ({
  className,
  size = 1,
  loadSearch,
  isReady,
  isLoading,
  drawerRef,
  updateEvent,
  searchEvents,
}) => {
  const [isOpen, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [results, setResult] = useState([])
  const [interval, setVal] = useState(null)
  const iconRef = useRef(null as HTMLAnchorElement)
  const userId = useSelector(getUserId)
  const premium = useSelector(isLicenseValid)
  const userPriceGroup = useSelector(getUserPriceGroup)
  const isAllInOnePriceGroup = useSelector(isUserNewPremium)
  const isAllInOneUser = useSelector(isAllInOneLicenseValid)
  const isAllInOneFreeUser = !isAllInOneUser && isAllInOnePriceGroup
  const isSidebarOpen = useSelector(
    (state: IStore) => state.bookingCalendar.isSidebarOpen,
  )
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)

  const handlePopoverOpen = event => {
    event.preventDefault()
    event.stopPropagation()
    setPopoverAnchor(event.currentTarget)
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
  }

  function reload() {
    if (!isSidebarOpen) {
      setOpen(false)
      setTerm('')
    }
  }

  useEffect(() => {
    setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerRef, isSidebarOpen])

  useEffect(() => {
    if (drawerRef && drawerRef.current) {
      drawerRef.current.addEventListener('mouseenter', reload)
    }
    return () => {
      if (drawerRef && drawerRef.current) {
        drawerRef.current.removeEventListener('mouseenter', reload)
      }
    }
  })

  useEffect(() => {
    if (!isReady && !isLoading && isOpen) {
      loadSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const focusRef = useCallback((node: HTMLElement) => {
    if (node !== null) {
      const inputs = node.getElementsByTagName('input')
      for (let input of inputs) {
        input.focus()
      }
    }
  }, [])

  const onSearchIconClick = (e) => {
    if ((premium && userPriceGroup !== 3) || isAllInOneFreeUser) {
      toggleSearchPopper(true)() // NOT IMPALED YET
    } else if (isMobile && !premium) {
      setIsModalOpened(true)
    } else {
      handlePopoverOpen(e)
    }
  }

  const toggleSearchPopper = (toggle: boolean) => () => {
    Mixpanel.people.increment(userId, 'Search Used', 1)
    Mixpanel.track('Search Used', {})
    setOpen(toggle)
  }

  const onSearchTerm = (term: string) => {
    setTerm(term)
  }

  const handleClose = useCallback(() => {
    toggleSearchPopper(false)()
    setTerm('')
  }, [])

  const onResultClick = useCallback(
    (id: IEvent['id']) => {
      Mixpanel.track('Search Result Used', {})
      Mixpanel.people.increment(userId, 'Search Results Used', 1)
      updateEvent(id)
      handleClose()
    },
    [updateEvent, userId, handleClose],
  )

  useDebounce(
    () => {
      if (term.length >= CHARACTERS_THRESHOLD) {
        const events = searchEvents(term)
        Mixpanel.track('Search Results Returned', {
          'Search Results Count': Number(events.length),
        })
        setResult(events)
      } else {
        setResult([])
      }
    },
    800,
    [term, isLoading],
  )

  return (
    <>
      <MenuLinkItem
        size={size}
        className={className}
        onClick={onSearchIconClick} // NOT IMPALED YET
        center={true}
        style={{ padding: `0 ${0.6 * size}em` }}
        ref={iconRef}
      >
        <FiSearch size={21 * size} />
      </MenuLinkItem>
      {!premium && !isAllInOneFreeUser && <PopoveredIcon2 anchorEl={popoverAnchor} onClose={handlePopoverClose} withIcon={false}/>}
      {isMobile && <UpgradePremiumBlockScreenModal
        open={isModalOpened}
        close={() => setIsModalOpened(false)}
      />}
      <SearchContainer
        size={size}
        isOpen={isOpen}
        iconRef={iconRef}
        handleClose={handleClose}
      >
        <SearchSummary
          size={size}
          onSearchTerm={onSearchTerm}
          term={term}
          focusRef={focusRef}
          isLoading={isLoading}
          results={results}
          onResultClick={onResultClick}
        />
      </SearchContainer>
    </>
  )
}

const SearchContainer = props => {
  const { children, size, isOpen, iconRef, handleClose } = props
  return (
    <SearchPopover
      size={size}
      open={isOpen}
      anchorRef={iconRef}
      onClose={handleClose}
    >
      {children}
    </SearchPopover>
  )
}

const SearchSummary = ({
  size,
  onSearchTerm,
  term,
  focusRef,
  isLoading,
  results,
  onResultClick,
}) => (
  <Flex column={true}>
    <SearchInput
      size={size}
      onTermChange={onSearchTerm}
      term={term}
      inputRef={focusRef}
    />
    {isLoading && <SearchLoader />}
    {term.length > 0 && !isLoading && (
      <>
        <SummaryBar
          size={size}
          count={results.length}
          maxResults={MAX_SEARCH_RESULTS}
        />
        <ResultsList
          size={size}
          results={results}
          maxResults={MAX_SEARCH_RESULTS}
          onResultClick={onResultClick}
        />
      </>
    )}
  </Flex>
)

export interface ISearchProps {
  className?: string
}

export interface ISearchStateProps {
  isLoading: boolean
  isReady: boolean
  drawerRef: React.RefObject<HTMLDivElement>
  isSidebarOpen?: boolean
  searchEvents: (term: string) => IEvent[]
}

export interface ISearchDispatchProps {
  loadSearch: () => void
  updateEvent: (id: IEvent['id']) => void
}

export default Search
